
Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault(ex) {
  return ex && typeof ex === 'object' && 'default' in ex ? ex.default : ex;
}

var filterX = _interopDefault(require('ml-array-xy-filter-x'));
var equallySpaced = _interopDefault(require('ml-array-xy-equally-spaced'));
var Stat = _interopDefault(require('ml-stat/array'));
var jcampconverter = require('jcampconverter');

/**
 *
 * @param {*} spectrum
 * @param {object} [options={}]
 *
 */

function getData(spectrum, options = {}) {
  const { filter } = options;

  let data = { x: [], y: [] };

  if (filter) {
    data = filterX(data, filter);
  }
  return data;
}

function getNormalized(spectrum, options = {}) {
  let {
    from = spectrum.x[0],
    to = spectrum.x[spectrum.x.length - 1],
    numberOfPoints = 1024,
    processes = [],
    exclusions = []
  } = options;

  let y = spectrum.y.slice(0);
  for (let process of processes) {
    switch (process.kind) {
      case 'centerMean': {
        let mean = Stat.mean(spectrum.y);
        let meanFct = (y) => y - mean;
        y = y.map(meanFct);
        break;
      }
      case 'scaleSD': {
        let std = Stat.standardDeviation(spectrum.y);
        let stdFct = (y) => y / std;
        y = y.map(stdFct);
        break;
      }
      default:
        console.error(`Unknown process kind: ${process.kind}`);
    }
  }

  let result = equallySpaced(
    { x: spectrum.x, y },
    { from, to, numberOfPoints, exclusions }
  );
  spectrum.normalized = result;

  return result;
}

/**
 * Class allowing manipulate one IR spectrum
 * @class spectrum
 * @param {object} [json={}] - object containing a spectrum
 * @param {Array} [json.x=[]] - x values
 * @param {Array} [json.y=[]] - y values
 */
class Spectrum {
  constructor(x, y, id, options = {}) {
    const { meta = {} } = options;
    if (!id) throw new Error('Spectrum: id is mandatory');

    if (x && x.length > 1 && x[0] > x[1]) {
      this.x = x.reverse();
      this.y = y.reverse();
    } else {
      this.x = x || [];
      this.y = y || [];
    }
    this.id = id;
    this.meta = meta;
    this.normalized = undefined;
  }

  getXY() {
    return { x: this.x, y: this.y };
  }
}

Spectrum.prototype.getData = function (options) {
  return getData(this, options);
};

Spectrum.prototype.updateNormalized = function (options) {
  this.normalized = getNormalized(this, options);
  return this.normalized;
};

Spectrum.fromJcamp = function (jcamp, id, meta) {};

function getJcampKind(data) {
  let datatype = data.spectra[0].dataType.toLowerCase();
  let xUnit = data.spectra[0].xUnit.toLowerCase();
  let yUnit = data.spectra[0].yUnit.toLowerCase();

  if (datatype.match(/infrared/)) {
    if (yUnit.match(/absorbance/)) {
      return IR_ABSORBANCE;
    } else {
      return IR_TRANSMITTANCE;
    }
  }
  return undefined;
}

const IR_TRANSMITTANCE = {
  normalization: {},
  importation: {
    converter: (transmittance) => -Math.log10(transmittance)
  },
  kind: 'Infrared',
  display: {
    xLabel: 'wavelength [cm-1]',
    xInverted: true,
    yLabel: 'Absorbance'
  }
};

const IR_ABSORBANCE = {
  normalization: {},
  kind: 'Infrared',
  display: {
    xLabel: 'wavelength [cm-1]',
    xInverted: true,
    yLabel: 'Absorbance'
  }
};

/**
 * Creates a new Chromatogram element based in a JCAMP string
 * @param {string} jcamp - String containing the JCAMP data
 * @return {Spectrum} - New class element with the given data
 */
function jcamp(jcamp) {
  const parsed = jcampconverter.convert(jcamp, { xy: true });
  const kind = getJcampKind(parsed);
  const data = parsed.spectra[0].data[0];
  // we convert the data
  if (kind && kind.importation && kind.importation.converter) {
    data.y = data.y.map(kind.importation.converter);
  }

  return { data, kind };
}

class SpectraProcessor {
  constructor(options = {}) {
    this.keepOriginalData =
      options.keepOriginalData === undefined ? false : options.keepOriginalData;
    this.normalizationFilter = undefined;
    this.spectra = [];
  }

  setNormalizationFilter(normalizationFilter = {}) {
    if (!this.keepOriginalData && this.spectra.length > 0) {
      throw new Error(
        'Can not change normalization filter, missing original data. Use the option keepOriginalData=true.'
      );
    }
    this.normalizationFilter = normalizationFilter;
    for (let spectrum of this.spectra) {
      spectrum.updateNormalized(this.normalizationFilter);
    }
  }

  /**
   * Add jcamp
   * @param {string} jcamp
   * @param {string} id
   * @param {boolean} [force=false]
   * @param {object} [meta={}]
   * @param {string} [meta.color]
   */
  addFromJcamp(jcamp$1, id, meta = {}, force = false) {
    if (force === false && this.contains(id)) {
      return;
    }

    let parsed = jcamp(jcamp$1);
    this.createAndAddSpectrum(parsed, id, meta);
  }

  /**
   * Add a spectrum
   * @param {Spectrum} spectrum
   */
  createAndAddSpectrum(parsed, id, meta) {
    let index = this.getSpectrumIndex(id);
    if (index === undefined) index = this.spectra.length;
    let spectrum = new Spectrum(parsed.data.x, parsed.data.y, id, {
      meta
    });
    spectrum.updateNormalized(this.normalizationFilter);
    this.spectra[index] = spectrum;
  }

  removeSpectrum(id) {
    let index = this.getSpectrumIndex(id);
    if (index === undefined) return undefined;
    return this.data.splice(index, 1);
  }

  contains(id) {
    return !isNaN(this.getSpectrumIndex(id));
  }

  getSpectrumIndex(id) {
    if (!id) return undefined;
    for (let i = 0; i < this.spectra.length; i++) {
      let spectrum = this.spectra[i];
      if (spectrum.id === id) return i;
    }
    return undefined;
  }

  getNormalizedData() {
    if (!this.spectra || !this.spectra[0]) return {};
    let matrix = [];
    let meta = [];
    let ids = [];
    for (let spectrum of this.spectra) {
      ids.push(spectrum.id);
      matrix.push(spectrum.normalized.y);
      meta.push(spectrum.meta);
    }
    let x = this.spectra[0].normalized.x;
    return { ids, matrix, meta, x };
  }

  getChart(options = {}) {
    const { ids, filter = {}, mode = this.mode } = options;
    let chart = {
      data: []
    };
    for (let spectrum of this.spectra) {
      if (!ids || ids.includes(spectrum.id)) {
        let data = spectrum.getData({ mode, filter });
        data.styles = {
          unselected: {
            lineColor: spectrum.meta.color || 'darkgrey',
            lineWidth: 1,
            lineStyle: 1
          },
          selected: {
            lineColor: spectrum.meta.color || 'darkgrey',
            lineWidth: 3,
            lineStyle: 1
          }
        };
        data.label = spectrum.meta.id || spectrum.id;
        chart.data.push(data);
      }
    }
    return chart;
  }
}

console.log('--------', { SpectraProcessor });

exports.SpectraProcessor = SpectraProcessor;
